import { IonIcon } from "@ionic/react";
import { closeOutline, receiptOutline, ticketOutline, timeOutline, trainOutline } from "ionicons/icons";
import { motion } from "motion/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore, purchasedItineraryOpenSlice, ticketPurchaseSlice, ticketViewerSlice, useEphemeralSelector } from "../../store/ephemeral";
import { purchasedItinerariesSlice, usePersistedSlice } from "../../store/persistence";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { triggerSuccessHaptic } from "../../tools/haptics.tools";
import { openInvoicePdf } from "../../tools/invoiceDownload.tools";
import { buildPurchasedItineraryEntry, upsertPurchasedItinerary } from "../../tools/purchasedItineraries.tools";
import { resolveItineraryDestinationLabel, resolveItineraryOriginLabel } from "../../tools/savedItineraryDisplay.tools";
import { formatPaymentMethod } from "../TicketViewer/ticketViewer.tools";
import { pollBookingOutcome } from "./payrexxReturn.tools";
import { TicketPurchaseLoader } from "./TicketPurchaseLoader";
import { readPurchaseError } from "./ticketPurchaseOfferRefresh.tools";

interface TicketBookingConfirmationProps {

    /** Dark UI adaptation. */
    "isDarkUi": boolean;

    /** Closes the purchase modal — the parent's `handleClose` also resets the wizard slice. */
    "onClose": () => void;
}

/**
 * Backend code meaning a booking is still running on another request (follow the attempt, never re-book).
 * `BOOKING_PENDING_VERIFICATION` is NOT included: it reports a definitive failure — the attempt is
 * `booking_unresolved`, tickets are never delivered, and the payment is released or refunded server-side.
 */
const BOOKING_IN_PROGRESS_CODE = "BOOKING_IN_PROGRESS";

/** Message shown when the backend definitively reports the booking unresolvable (`BOOKING_PENDING_VERIFICATION`). */
const BOOKING_NOT_CONFIRMED_MESSAGE = "L'achat n'a pas pu être confirmé. Aucun billet ne sera émis ; la retenue sur votre moyen de paiement est levée ou le montant payé sera remboursé.";

/**
 * Message shown when the booking poll budget is exhausted while the attempt is still
 * `booking_in_progress` server-side: the backend may still complete — delivered tickets
 * then appear in « Mes billets » — otherwise the payment is released or refunded. The
 * payment status is not re-read here: a captured payment would produce a wrong
 * "nothing was debited" line.
 */
const BOOKING_STILL_VERIFYING_MESSAGE = "La confirmation de votre achat prend plus de temps que prévu. Si elle aboutit, vos billets apparaîtront dans « Mes billets » ; sinon, le montant réservé est automatiquement libéré ou remboursé.";

/**
 * Human-readable wording of a definitive booking failure.
 *
 * @param code - Backend error code, if any.
 * @returns French failure message.
 */
function describeBookingFailure( code: string | null ): string {
    switch ( code ) {
        case "PAYMENT_CAPTURE_FAILED":
            return "Le paiement n'a pas pu être encaissé ; la retenue sur votre moyen de paiement a été annulée.";

        case "PAYMENT_CONFIRMATION_MISSING":
            return "Le paiement n'est plus confirmé.";

        case "PURCHASE_ATTEMPT_CLOSED":
            return "Cet achat est clôturé.";

        default:
            return "L'émission des billets a échoué.";
    }
}

export const TicketBookingConfirmation: React.FC<TicketBookingConfirmationProps> = ( { isDarkUi, onClose } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const state = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s );
    const { prebookingResponse } = state;

    /** Guards the booking call against React StrictMode double-invocation. */
    const bookingStartedRef = useRef( false );

    /** Guards the purchased-entry write against double effect invocation. */
    const persistedRef = useRef( false );

    /** Guards the success haptic against double effect invocation. */
    const hapticFiredRef = useRef( false );
    const [ , setPurchasedItineraries ] = usePersistedSlice( purchasedItinerariesSlice );

    /**
     * Fetches the authoritative payment status after a booking failure so the
     * compensation state (released reservation vs manual handling vs refund)
     * can be shown. With reservation gateways nothing is charged before the
     * booking commits, so the nominal message says no amount was debited.
     *
     * @param attemptId - Purchase attempt id.
     * @returns Compensation wording matching the payment status.
     */
    const resolveRefundMessage = useCallback( async( attemptId: string ): Promise<string> => {
        try {
            const payment = await TicketPurchaseApi.postPurchasePaymentVerify( { "purchaseAttemptId": attemptId } );

            if ( payment.status === "released" ) {
                return "Aucun montant n'a été débité ; la retenue sur votre moyen de paiement a été levée.";
            }

            if ( payment.status === "refunded" ) {
                return "Le montant payé a été intégralement remboursé.";
            }

            if ( payment.status === "refund_pending" ) {
                return "Le remboursement du paiement est en cours.";
            }

            if ( payment.status === "to_capture" ) {
                return "Le paiement est en cours de régularisation par notre équipe.";
            }

            if ( payment.status === "to_refund" || payment.status === "refund_failed" ) {
                return "Votre paiement est en cours de vérification — aucun montant ne sera débité sans billet émis.";
            }
        } catch {
            // Status re-check failure must not hide the booking error itself.
        }

        return "Aucun montant n'a été débité.";
    }, [] );

    /**
     * Polls the attempt while the backend books or verifies it, then shows the outcome.
     *
     * @param attemptId - Purchase attempt id.
     */
    const awaitBookingOutcome = useCallback( async( attemptId: string ): Promise<void> => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "bookingStatus": "verifying",
            "canClose":      true,
            "error":         null,
        } ) );

        const outcome = await pollBookingOutcome( attemptId );

        if ( outcome.kind === "booked" ) {
            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "bookingStatus": "succeeded",
                "booking":       outcome.booking,
                "canClose":      true,
                "error":         null,
            } ) );

            return;
        }

        if ( outcome.kind === "pending" ) {
            // The backend may still be completing the booking: an inconclusive outcome — never a definitive failure, and no purchase restart (a late success would double-book it).
            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "bookingStatus": "unconfirmed",
                "canClose":      true,
                "error":         BOOKING_STILL_VERIFYING_MESSAGE,
            } ) );

            return;
        }

        if ( outcome.kind === "failed" ) {
            const refundMessage = await resolveRefundMessage( attemptId );

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "bookingStatus": "failed",
                "canClose":      true,
                "error":         `L'achat n'a pas pu aboutir. ${ refundMessage }`,
            } ) );

            return;
        }
    }, [ resolveRefundMessage ] );

    const book = useCallback( async() => {
        if ( prebookingResponse === null ) {
            return;
        }

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "bookingStatus": "loading",
            "canClose":      false,
            "error":         null,
        } ) );

        try {
            const booking = await TicketPurchaseApi.postPurchaseBooking( { "purchaseAttemptId": prebookingResponse.purchaseAttemptId } );

            /*
             * The backend answers `booked`/`ticketFilesComplete: false` when the first
             * file-download pass missed files: keep the loader and poll until the
             * attempt reaches `tickets_fetched` — success is only shown once the
             * tickets carry their barcode and full metadata.
             */
            if ( !booking.ticketFilesComplete && booking.status !== "tickets_fetched" ) {
                await awaitBookingOutcome( booking.purchaseAttemptId );

                return;
            }

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "bookingStatus": "succeeded",
                "booking":       booking,
                "canClose":      true,
            } ) );
        } catch( error ) {
            const { status, code } = readPurchaseError( error );

            /*
             * A booking still running on another request (or a response lost on the network):
             * never retry `/book`, follow the attempt instead. `BOOKING_PENDING_VERIFICATION`
             * is a definitive failure — tickets are never delivered for an unresolved attempt.
             */
            if ( status === null || code === BOOKING_IN_PROGRESS_CODE ) {
                await awaitBookingOutcome( prebookingResponse.purchaseAttemptId );

                return;
            }

            const refundMessage = await resolveRefundMessage( prebookingResponse.purchaseAttemptId );

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "bookingStatus": "failed",
                "canClose":      true,
                "error":         code === "BOOKING_PENDING_VERIFICATION" ? `${ BOOKING_NOT_CONFIRMED_MESSAGE } ${ refundMessage }` : `${ describeBookingFailure( code ) } ${ refundMessage }`,
            } ) );
        }
    }, [ awaitBookingOutcome, prebookingResponse, resolveRefundMessage ] );

    /**
     * Starts a new purchase on the same itinerary after a definitive failure
     * (the failed attempt is closed server-side and cannot be retried).
     */
    const restartPurchase = useCallback( () => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "step":                  "selection",
            "selectionSegmentIndex": 0,
            "selectedOffers":        {},
            "prebookingResponse":    null,
            "purchaseAttemptId":     null,
            "paymentId":             null,
            "payrexxUrl":            null,
            "paymentStatus":         "idle",
            "bookingStatus":         "idle",
            "booking":               null,
            "error":                 null,
            "notice":                null,
            "canClose":              true,
        } ) );
    }, [] );

    useEffect( () => {
        // StrictMode mounts the component twice; the booking call must run once.
        if ( bookingStartedRef.current ) {
            return;
        }
        bookingStartedRef.current = true;

        // `/book` only runs from `idle`: a resumed attempt whose booking already started is followed instead.
        if ( state.bookingStatus === "idle" ) {
            void book();
        } else if ( state.bookingStatus === "verifying" && prebookingResponse !== null ) {
            void awaitBookingOutcome( prebookingResponse.purchaseAttemptId );
        }
    }, [ awaitBookingOutcome, book, prebookingResponse, state.bookingStatus ] );

    useEffect( () => {
        /*
         * Only a definitive synchronous emission creates a purchased entry —
         * payment authorization, prebooking and unresolved/failed outcomes never do.
         */
        if (
            state.bookingStatus !== "succeeded" ||
            state.booking === null ||
            state.itinerary === null ||
            prebookingResponse === null ||
            persistedRef.current
        ) {
            return;
        }
        persistedRef.current = true;

        const entry = buildPurchasedItineraryEntry( state.itinerary, {
            "purchaseAttemptId": state.booking.purchaseAttemptId,
            "bookingId":         state.booking.bookingId,
            "invoiceNumber":     state.booking.invoiceNumber,
            "creditNotes":       state.booking.creditNotes,
            "provider":          state.booking.provider,
            "providerBookingId": state.booking.providerBookingId,
            "conversationId":    state.booking.conversationId,
            "purchasedAt":       Date.now(),
            "segments":          prebookingResponse.prebookings.map( ( prebooking ) => ( {
                "preBookingId":    prebooking.preBookingId,
                "segmentId":       prebooking.segmentId,
                "offerIdentifier": prebooking.offerIdentifier,
                "validityPeriod":  prebooking.validityPeriod,
            } ) ),
            "tickets":             state.booking.tickets,
            "ticketFilesComplete": state.booking.ticketFilesComplete,
            "totalPriceCents":     Math.round( prebookingResponse.totalPrice * 100 ),
            "currency":            prebookingResponse.currency,
            "paymentMethod":       state.booking.paymentMethod,
        } );
        setPurchasedItineraries( ( previous ) => upsertPurchasedItinerary( previous, entry ) );

        /*
         * Ask the planner to select the purchased entry (saved-list "purchased"
         * mode) instead of leaving the standard itinerary displayed.
         */
        if ( state.itinerary.id !== undefined && state.itinerary.id !== null ) {
            ephemeralStore.setValue( purchasedItineraryOpenSlice, {
                "itineraryId": state.itinerary.id,
                "revision":    ephemeralStore.peek( purchasedItineraryOpenSlice ).revision + 1,
            } );
        }
    }, [ prebookingResponse, setPurchasedItineraries, state.booking, state.bookingStatus, state.itinerary ] );

    useEffect( () => {
        if ( state.bookingStatus === "succeeded" && hapticFiredRef.current === false ) {
            hapticFiredRef.current = true;
            void triggerSuccessHaptic();
        }
    }, [ state.bookingStatus ] );

    /**
     * Closes the wizard then opens the ticket viewer on the freshly persisted entry —
     * closing first avoids stacking two `SheetModal`s.
     */
    const openTicketViewer = useCallback( () => {
        const itineraryId = state.itinerary?.id ?? null;
        onClose();

        if ( itineraryId !== null ) {
            ephemeralStore.setValue( ticketViewerSlice, {
                "isOpen":           true,
                "itineraryId":      itineraryId,
                "initialSegmentId": null,
            } );
        }
    }, [ onClose, state.itinerary ] );

    /**
     * Downloads and opens the invoice PDF (new tab on web, share sheet on native).
     * Disabled while the booking carries no invoice key — issuance is best-effort
     * server-side, so the button stays hidden when `bookingId` is absent.
     */
    const [ invoiceError, setInvoiceError ] = useState<string | null>( null );
    const openInvoice = useCallback( (): void => {
        const bookingId = state.booking?.bookingId;
        if ( bookingId === undefined || bookingId === null ) {
            return;
        }

        setInvoiceError( null );
        void openInvoicePdf( bookingId, state.booking?.invoiceNumber ).then( ( opened ) => {
            if ( !opened ) {
                setInvoiceError( "La facture n'est pas encore disponible. Réessayez dans un instant." );
            }
        } );
    }, [ state.booking ] );

    const contentBackground = isDarkUi ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.05)";
    const cardBackground = palette.modalCardBackground;

    if ( state.bookingStatus === "loading" || state.bookingStatus === "idle" || state.bookingStatus === "verifying" ) {
        return (
            <div
                className="flex h-full flex-col items-center justify-center overflow-y-auto p-6 text-center"
                style={{ "backgroundColor": contentBackground }}
            >
                <TicketPurchaseLoader isDarkUi={isDarkUi} />
                <p
                    className="mt-4 text-xs"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    Émission de vos billets en cours...
                </p>
            </div>
        );
    }

    /*
     * Inconclusive outcome (poll budget expired, booking still running server-side):
     * no « Recommencer l'achat » — a late backend success would double-book it.
     * The shopper can only close and check « Mes billets » later.
     */
    if ( state.bookingStatus === "unconfirmed" ) {
        return (
            <div
                className="flex h-full flex-col p-4 md:p-6"
                style={{ "backgroundColor": contentBackground }}
            >
                <div
                    className="flex-1 rounded-2xl border p-6 text-center"
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <IonIcon
                            className="h-7 w-7"
                            icon={timeOutline}
                        />
                    </div>
                    <h3 className="mb-2 text-sm font-bold text-amber-600">
Confirmation en cours
                    </h3>
                    <p className="text-xs opacity-80">
{state.error}
                    </p>
                </div>
                <div
                    className="shrink-0 border-t pt-3"
                    style={{ "borderColor": palette.overlayBorder }}
                >
                    <div className="flex justify-end gap-2">
                        <button
                            className="min-h-[48px] rounded-xl px-5 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                            onClick={onClose}
                            style={{ "color": palette.overlayTextSecondary }}
                            type="button"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if ( state.bookingStatus === "failed" ) {
        return (
            <div
                className="flex h-full flex-col p-4 md:p-6"
                style={{ "backgroundColor": contentBackground }}
            >
                <div
                    className="flex-1 rounded-2xl border p-6 text-center"
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <svg
                            className="h-7 w-7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                d="M6 18L18 6M6 6l12 12"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <h3 className="mb-2 text-sm font-bold text-red-600">
Achat échoué
                    </h3>
                    <p className="text-xs opacity-80">
{state.error}
                    </p>
                </div>
                <div
                    className="shrink-0 border-t pt-3"
                    style={{ "borderColor": palette.overlayBorder }}
                >
                    <div className="flex justify-end gap-2">
                        <button
                            className="min-h-[48px] rounded-xl px-5 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                            onClick={onClose}
                            style={{ "color": palette.overlayTextSecondary }}
                            type="button"
                        >
                            Fermer
                        </button>
                        <button
                            className="min-h-[48px] rounded-xl px-5 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                            onClick={restartPurchase}
                            style={{
                                "backgroundColor": palette.brandPrimary,
                                "color":           palette.textOnBrandPrimary,
                            }}
                            type="button"
                        >
                            Recommencer l&apos;achat
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const receiptRoute = state.itinerary === null
        ? null
        : `${ resolveItineraryOriginLabel( state.itinerary ) } → ${ resolveItineraryDestinationLabel( state.itinerary ) }`;
    const receiptPayment = formatPaymentMethod( state.booking?.paymentMethod );
    const receiptTicketCount = state.booking?.tickets.length ?? 0;
    const receiptSummaryParts = [
        `${ receiptTicketCount } billet${ receiptTicketCount > 1 ? "s" : "" }`,
        prebookingResponse === null ? null : `${ prebookingResponse.totalPrice.toFixed( 2 ) } ${ prebookingResponse.currency }`,
        receiptPayment === "" ? null : receiptPayment,
    ].filter( ( part ): part is string => part !== null );

    return (
        <div className="flex h-full flex-col">
            <div
                className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-6 text-center"
                style={{ "backgroundColor": contentBackground }}
            >
                <motion.div
                    animate={{
 "opacity": 1,
"scale":   1,
}}
                    className="mb-4 flex h-14 w-14 items-center justify-center rounded-full text-white"
                    initial={reducedMotion
? false
: {
 "opacity": 0,
"scale":   0,
}}
                    style={{ "backgroundColor": palette.brandPrimary }}
                    transition={{
 "damping":   20,
"stiffness": 300,
"type":      "spring",
}}
                >
                    <svg
                        className="h-7 w-7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M5 13l4 4L19 7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </motion.div>
                <h3 className="mb-1 text-sm font-bold tracking-tight">
                    Achat réussi
                </h3>
                <p
                    className="text-xs"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    Vos billets ont été émis.
                </p>
                {state.booking !== null && (
                    <div
                        className="mt-5 w-full max-w-sm rounded-2xl border p-4 text-left"
                        data-testid="booking-success-receipt"
                        style={{
                            "backgroundColor": cardBackground,
                            "borderColor":     palette.overlayBorder,
                        }}
                    >
                        {receiptRoute !== null && (
                            <div
                                className="flex items-center gap-2 text-sm font-semibold"
                                data-testid="booking-success-route"
                            >
                                <IonIcon
                                    className="text-base"
                                    icon={trainOutline}
                                />
                                <span>
                                    {receiptRoute}
                                </span>
                            </div>
                        )}
                        <div
                            className={receiptRoute === null ? "text-xs" : "mt-1.5 text-xs"}
                            data-testid="booking-success-summary"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            {receiptSummaryParts.join( " · " )}
                        </div>
                    </div>
                )}
                <p
                    className="mt-4 max-w-sm text-[11px]"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    Retrouvez vos billets à tout moment depuis l&apos;itinéraire.
                </p>
            </div>
            <div
                className="shrink-0 space-y-2 border-t p-4"
                style={{ "borderColor": palette.overlayBorder }}
            >
                <button
                    className="flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-xl px-6 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                    data-testid="booking-show-tickets"
                    onClick={openTicketViewer}
                    style={{
                        "backgroundColor": palette.brandPrimary,
                        "color":           palette.textOnBrandPrimary,
                    }}
                    type="button"
                >
                    <IonIcon icon={ticketOutline} />
                    Voir mes billets
                </button>
                <button
                    className="flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-xl border px-6 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                    data-testid="booking-show-invoice"
                    onClick={openInvoice}
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                    type="button"
                >
                    <IonIcon icon={receiptOutline} />
                    Voir la facture
                </button>
                {invoiceError !== null && (
                    <p
                        className="px-1 text-center text-[11px]"
                        data-testid="booking-invoice-error"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        {invoiceError}
                    </p>
                )}
                <button
                    className="flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-xl px-6 py-2.5 text-sm font-bold transition-opacity hover:opacity-90"
                    data-testid="booking-close"
                    onClick={onClose}
                    style={{ "color": palette.overlayTextSecondary }}
                    type="button"
                >
                    <IonIcon icon={closeOutline} />
                    Fermer
                </button>
            </div>
        </div>
    );
};
